import { createTRPCClient, httpBatchLink } from '@trpc/client';
import { createTRPCContext, createTRPCOptionsProxy } from '@trpc/tanstack-react-query';
import type { AppRouter } from '@server/trpc/router';
import type { inferRouterInputs, inferRouterOutputs } from '@trpc/server';
import { config } from '@/config/env';
import { isWeb } from '@/lib/platform';
import { buildAuthHeaders, queryClient } from './queryClient';

/** Convenience helpers for the inferred tRPC input/output types. */
export type RouterInputs = inferRouterInputs<AppRouter>;
export type RouterOutputs = inferRouterOutputs<AppRouter>;

/**
 * tRPC integration built on the TanStack React Query client
 * (https://trpc.io/blog/introducing-tanstack-react-query-client).
 *
 * This is the "bring your own useQuery" model: `useTRPC()` returns an options
 * proxy whose `.queryOptions()` / `.mutationOptions()` factories are passed into
 * TanStack Query's native hooks, so it slots into the existing query setup.
 */
export const { TRPCProvider, useTRPC, useTRPCClient } = createTRPCContext<AppRouter>();

/**
 * Vanilla tRPC client. Auth headers + credentials mode are ported from the REST
 * fetch wrapper in queryClient.ts so cookie/expo-origin handling stays identical.
 */
export const trpcClient = createTRPCClient<AppRouter>({
  links: [
    httpBatchLink({
      url: `${config.apiUrl}/trpc`,
      headers() {
        return buildAuthHeaders() as Record<string, string>;
      },
      fetch(input, init) {
        return fetch(input as RequestInfo, {
          ...(init as RequestInit),
          credentials: isWeb ? 'include' : 'omit',
        });
      },
    }),
  ],
});

/**
 * Options proxy for use outside React (e.g. the background refresh task), bound to
 * the shared QueryClient. Gives typed `queryOptions()`/`queryKey()` for
 * `fetchQuery`/`setQueryData` without React hooks.
 */
export const trpc = createTRPCOptionsProxy<AppRouter>({
  client: trpcClient,
  queryClient,
});
